import { useCallback, useMemo } from 'react';
import { useAccessRequestNodeFragment } from '@frameio/access-requests/gql/fragments/generated/AccessRequestNode';
import { useAccessRequestsActions } from '@frameio/access-requests/hooks/useAccessRequestsActions';
import { useCanApproveAccessRequest } from '@frameio/access-requests/hooks/useCanApproveAccessRequest';
import { useGrantedProject } from '@frameio/access-requests/hooks/useGrantedProject';
import { useAccountAdobeManagedDetailsFragment } from '@frameio/graphql-client/generated';
import { isAccountAdobeManaged } from '@frameio/graphql-client/utils/isAccountAdobeManaged';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing/getLink';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { Box, Button2, ListItemContent } from '@frameio/vapor';
import { NOTIFICATION_TYPE_ACCESS_REQUEST_SUBMITTED } from '../../constants';
import { Avatars } from '../NotificationCell/Avatars/Avatars';
import { Footer } from '../NotificationCell/Footer/Footer';
import styles from '../NotificationCell/Footer/Footer.module.css';
import { Header } from '../NotificationCell/Header/Header';
import { Body } from './Body';
import TEXT from './generated/TEXT/AccessRequestCTAs';

TEXT.DEFINE({
  /** Button label to approve an access request */
  approve: 'Approve',
  /** Button label to decline an access request */
  decline: 'Decline',
  /** Button label to navigate to plan management */
  manage: 'Manage Plan',
});

export function AccessRequestCell({ id }: { id: string }) {
  const accountId = useActiveAccountId();
  const { data, complete } = useAccessRequestNodeFragment({
    from: {
      id,
      __typename: 'AccessRequest',
    },
  });
  const { hasAccountUser, requesterUser, requestedResource, hasMemberLicense } =
    data;
  const grantedProject = useGrantedProject({ id });

  const { handleApprove, optimisticallyDeclineRequest } =
    useAccessRequestsActions({
      id,
      grantedProject,
      requesterUser,
      requestedResource,
      hasAccountUser,
      hasMemberLicense,
    });

  const { data: adobeAccountData } = useAccountAdobeManagedDetailsFragment({
    from: {
      __typename: 'Account',
      id: accountId,
    },
  });

  const isAdobeManagedAccount = isAccountAdobeManaged(
    adobeAccountData?.managedBy
  );
  const adobeOrgId = adobeAccountData?.adobeOrg ?? null;
  const { canApprove } = useCanApproveAccessRequest({
    hasMemberLicense,
  });
  const track = useTrackUnsafe();

  const accessRequestTrackingProps = useMemo(
    () => ({
      access_request_id: id,
      notification_type: 'InApp',
      is_internal_user: hasAccountUser,
      is_adobe_managed_account: isAdobeManagedAccount,
      ...(adobeOrgId && {
        adobe_org_id: adobeOrgId,
      }),
    }),
    [id, hasAccountUser, isAdobeManagedAccount, adobeOrgId]
  );

  const trackButtonClick = useCallback(() => {
    track('access-request-notification-actioned', {
      ...accessRequestTrackingProps,
    });
  }, [track, accessRequestTrackingProps]);

  const declineText = TEXT.decline;
  const approveText = TEXT.approve;
  const manageText = TEXT.manage;

  const FooterActions = useMemo(
    () => (
      <Box
        display="flex"
        flexWrap="wrap"
        gap={8}
        justifyContent="center"
        paddingTop={12}
      >
        <Button2
          className={styles.buttonCta}
          data-testid="decline-access-request-button"
          minWidth="auto"
          onPress={() => {
            trackButtonClick();
            optimisticallyDeclineRequest();
          }}
          size="small"
          variant="secondary"
        >
          {declineText}
        </Button2>
        {canApprove ? (
          <Button2
            className={styles.buttonCta}
            data-testid="approve-access-request-button"
            minWidth="auto"
            onPress={() => {
              trackButtonClick();
              handleApprove();
            }}
            size="small"
            variant="primary"
          >
            {approveText}
          </Button2>
        ) : (
          <Button2
            className={styles.buttonCta}
            data-testid="manage-plan-button"
            elementType="a"
            href={getLink({ page: 'settings', section: 'plan' })}
            minWidth="auto"
            size="small"
            variant="primary"
          >
            {manageText}
          </Button2>
        )}
      </Box>
    ),
    [
      canApprove,
      handleApprove,
      optimisticallyDeclineRequest,
      trackButtonClick,
      declineText,
      approveText,
      manageText,
    ]
  );

  if (!data || !complete) return null;
  return (
    <ListItemContent
      adornmentBefore={
        <Avatars
          id={id}
          notificationType={NOTIFICATION_TYPE_ACCESS_REQUEST_SUBMITTED}
        />
      }
      data-testid="access-request-cell"
      description={<Body id={id} />}
      label={
        <Header
          id={id}
          notificationType={NOTIFICATION_TYPE_ACCESS_REQUEST_SUBMITTED}
        />
      }
      meta={
        <Footer
          actions={FooterActions}
          id={id}
          notificationType={NOTIFICATION_TYPE_ACCESS_REQUEST_SUBMITTED}
        />
      }
    />
  );
}
